/**
 * @file SearchRepeatingBills.js
 * @version 1.2.5
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-27
 * @modified 2026-10-01
 * @description Search and view repeating bills (managed in Xero)
 */

import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './SearchRepeatingBills.css';
import './TableStyles.css';
import Dropdown from './Dropdown';
import Input from './Input';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import LoadingBar from './LoadingBar';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import RepeatingBillModal from './RepeatingBillModal';
import { getTaxPercentDisplay } from '../utils/taxTypes';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import { useSlideOutButton } from './SlideOutButton';
import { useDragSelect } from '../hooks/useDragSelect';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight (utils/activeFilters.js).
 */
const CLEARED_FILTERS = { search: '', status: 'all', limit: '20' };

function SearchRepeatingBills({ user }) {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBillId, setSelectedBillId] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  // Expandable row state
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [lineItems, setLineItems] = useState({});
  const [loadingLineItems, setLoadingLineItems] = useState({});
  const [expandingAll, setExpandingAll] = useState(false);

  // Bulk selection state
  const [selectedBills, setSelectedBills] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkActionsVisible, setBulkActionsVisible] = useState(false);
  const [bulkActionsClosing, setBulkActionsClosing] = useState(false);

  // Slideout buttons
  useSlideOutButton([bulkActionsVisible]);

  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    limit: '20'
  });

  const [sortField, setSortField] = useState('next_scheduled_date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });

  // Sync state
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('');

  const isInitialMount = useRef(true);

  useEffect(() => {
    searchRepeatingBills(1);
    setTimeout(() => { isInitialMount.current = false; }, 350);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialMount.current) return;
    const timeoutId = setTimeout(() => { searchRepeatingBills(1); }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, filters.limit, sortField, sortDirection]);

  // Bulk selection visibility
  useEffect(() => {
    if (selectedBills.size > 0 && !bulkActionsVisible) {
      setBulkActionsVisible(true);
      setBulkActionsClosing(false);
    } else if (selectedBills.size === 0 && bulkActionsVisible) {
      setBulkActionsClosing(true);
      setTimeout(() => { setBulkActionsVisible(false); setBulkActionsClosing(false); }, 250);
    }
  }, [selectedBills.size, bulkActionsVisible]);

  // Typing fires a search per pause and the responses can land out of order —
  // only the newest may write the table.
  const latest = useLatestRequest();

  const searchRepeatingBills = async (page = 1) => {
    const seq = latest.begin();
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: filters.limit,
        sortField,
        sortDirection
      });

      if (filters.search) params.append('search', filters.search);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);

      const response = await fetch(`${API_URL}/api/repeating-bills/search?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (!latest.isLatest(seq)) return;   // superseded by a later search

      if (data.success) {
        setBills(data.repeatingBills || []);
        setPagination({ page: data.currentPage, limit: parseInt(filters.limit), total: data.total, totalPages: data.totalPages });
      }
    } catch (error) {
      if (!latest.isLatest(seq)) return;
      setAlertDialog({ isOpen: true, message: 'Failed to search repeating bills: ' + error.message, type: 'error' });
    } finally {
      if (latest.isLatest(seq)) setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const filtersApplied = activeHeaderFilters(filters, CLEARED_FILTERS).count > 0;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      searchRepeatingBills(newPage);
    }
  };

  // Sync from Xero
  const syncRepeatingBills = async () => {
    setLoading(true);
    setSyncProgress(0);
    setSyncMessage('Initiating sync...');

    try {
      const eventSource = new EventSource(
        `${API_URL}/api/repeating-bills/sync`,
        { withCredentials: true }
      );

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'progress') {
          const progress = data.totalPages > 0
            ? Math.min(95, Math.round((data.currentPage / data.totalPages) * 100))
            : 50;
          setSyncProgress(progress);
          setSyncMessage(data.status || `Fetching... (${data.recordsFetched} records)`);
        } else if (data.type === 'complete') {
          setSyncProgress(100);
          setSyncMessage('Sync complete!');
          setAlertDialog({ isOpen: true, message: data.result.message || 'Sync complete', type: 'success' });
          searchRepeatingBills(1);
          eventSource.close();
          setTimeout(() => { setLoading(false); setSyncProgress(0); setSyncMessage(''); }, 1000);
        } else if (data.type === 'error') {
          setAlertDialog({ isOpen: true, message: `Sync failed: ${data.error}`, type: 'error' });
          eventSource.close();
          setLoading(false);
          setSyncProgress(0);
          setSyncMessage('');
        }
      };

      eventSource.onerror = () => {
        setAlertDialog({ isOpen: true, message: 'Connection to server lost during sync', type: 'error' });
        eventSource.close();
        setLoading(false);
        setSyncProgress(0);
        setSyncMessage('');
      };
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to sync: ${error.message}`, type: 'error' });
      setLoading(false);
      setSyncProgress(0);
      setSyncMessage('');
    }
  };

  // Expand/collapse rows
  const toggleRowExpand = async (billId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(billId)) {
      newExpanded.delete(billId);
    } else {
      newExpanded.add(billId);
      if (!lineItems[billId]) await fetchLineItems(billId);
    }
    setExpandedRows(newExpanded);
  };

  const fetchLineItems = async (billId) => {
    setLoadingLineItems(prev => ({ ...prev, [billId]: true }));
    try {
      const response = await fetch(`${API_URL}/api/repeating-bills/${billId}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setLineItems(prev => ({ ...prev, [billId]: data.repeatingBill?.lines || [] }));
      }
    } catch (error) {
      console.error('Failed to fetch line items:', error);
      setLineItems(prev => ({ ...prev, [billId]: [] }));
    } finally {
      setLoadingLineItems(prev => ({ ...prev, [billId]: false }));
    }
  };

  const handleExpandAll = async () => {
    if (expandedRows.size === bills.length && bills.length > 0) {
      setExpandedRows(new Set());
      return;
    }
    setExpandingAll(true);
    const idsToFetch = bills.filter(b => !lineItems[b.id]).map(b => b.id);
    if (idsToFetch.length > 0) {
      await Promise.all(idsToFetch.map(async (id) => {
        setLoadingLineItems(prev => ({ ...prev, [id]: true }));
        try {
          const response = await fetch(`${API_URL}/api/repeating-bills/${id}`, { credentials: 'include' });
          const data = await response.json();
          if (data.success) setLineItems(prev => ({ ...prev, [id]: data.repeatingBill?.lines || [] }));
        } catch { setLineItems(prev => ({ ...prev, [id]: [] })); }
        finally { setLoadingLineItems(prev => ({ ...prev, [id]: false })); }
      }));
    }
    setExpandedRows(new Set(bills.map(b => b.id)));
    setExpandingAll(false);
  };

  // Bulk selection
  const dragSelect = useDragSelect({
    setChecked: (id, checked) => {
      setSelectedBills(prev => {
        const next = new Set(prev);
        if (checked) next.add(id); else next.delete(id);
        return next;
      });
    }
  });

  const toggleSelectBill = (id) => {
    setSelectedBills(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedBills.size === bills.length) {
      setSelectedBills(new Set());
    } else {
      setSelectedBills(new Set(bills.map(b => b.id)));
    }
  };

  // Delete
  const handleDeleteBill = (bill) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Repeating Bill',
      message: `Are you sure you want to delete the repeating bill for ${bill.supplier_name || 'this supplier'}?`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const response = await fetch(`${API_URL}/api/repeating-bills/${bill.id}`, {
            method: 'DELETE', credentials: 'include'
          });
          const data = await response.json();
          if (data.success) {
            setAlertDialog({ isOpen: true, message: 'Repeating bill deleted', type: 'success' });
            searchRepeatingBills(pagination.page);
          } else {
            setAlertDialog({ isOpen: true, message: data.error || 'Failed to delete', type: 'error' });
          }
        } catch (error) {
          setAlertDialog({ isOpen: true, message: 'Failed to delete repeating bill', type: 'error' });
        }
      }
    });
  };

  // Bulk delete
  const handleBulkDelete = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Selected',
      message: `Delete ${selectedBills.size} repeating bill${selectedBills.size !== 1 ? 's' : ''}?`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        setBulkActionLoading(true);
        try {
          let success = 0;
          for (const id of selectedBills) {
            const response = await fetch(`${API_URL}/api/repeating-bills/${id}`, {
              method: 'DELETE', credentials: 'include'
            });
            const data = await response.json();
            if (data.success) success++;
          }
          setAlertDialog({ isOpen: true, message: `Deleted ${success} repeating bill${success !== 1 ? 's' : ''}`, type: 'success' });
          setSelectedBills(new Set());
          searchRepeatingBills(pagination.page);
        } catch (error) {
          setAlertDialog({ isOpen: true, message: 'Failed to delete some bills', type: 'error' });
        } finally {
          setBulkActionLoading(false);
        }
      }
    });
  };

  // Helpers
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(amount || 0);

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getFullYear()).slice(-2)}`;
  };

  const formatSchedule = (bill) => {
    if (!bill.schedule_period || !bill.schedule_unit) return '-';
    const unit = (bill.schedule_unit || '').toLowerCase();
    const period = bill.schedule_period;
    const unitLabel = unit === 'monthly' ? 'month' : unit === 'weekly' ? 'week' : unit;
    return `Every ${period} ${unitLabel}${period > 1 ? 's' : ''}`;
  };

  const getStatusBadgeClass = (status) => {
    const map = { DRAFT: 'draft', AUTHORISED: 'authorised', DELETED: 'deleted' };
    return `status-badge ${map[(status || '').toUpperCase()] || 'default'}`;
  };

  return (
    <div className="search-repeating-bills">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Search and view repeating bills (managed in Xero)</span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={() => {
                  setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '20' }));
                  setSortField('next_scheduled_date');
                  setSortDirection('asc');
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            {user?.is_admin && (
              <div>
                <button onClick={syncRepeatingBills} className="btn-slideout admin-item" disabled={loading}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">sync</span></div>
                  <span className="btn-label">Sync from Xero</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row repeating-bills-filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp
                amounts
                fields={['supplier', 'reference', 'contact', 'account no.', 'total']}
              />
            </label>
            <Input type="text" placeholder="Supplier, reference..." value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onEnter={() => searchRepeatingBills(1)} icon="search" />
          </div>
          <div className="filter-group">
            <label>Status</label>
            <Dropdown value={filters.status} onChange={(v) => handleFilterChange('status', v)}
              options={[
                { value: 'all', label: 'All Active' },
                { value: 'DRAFT', label: 'Draft' },
                { value: 'AUTHORISED', label: 'Authorised' },
                { value: 'DELETED', label: 'Deleted' }
              ]} placeholder="Select status..." />
          </div>
          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown value={filters.limit} onChange={(v) => handleFilterChange('limit', v)}
              options={[{ value: '20', label: '20' }, { value: '50', label: '50' }, { value: '100', label: '100' }]}
              placeholder="20" />
          </div>
        </div>
      </div>

      {syncProgress > 0 && (
        <div className="loading-bar-wrapper">
          <LoadingBar progress={syncProgress} message={syncMessage} />
        </div>
      )}

      {bulkActionsVisible && (
        <div className={`bulk-actions-bar ${bulkActionsClosing ? 'closing' : ''}`}>
          <div className="bulk-actions-info">
            <span className="bulk-actions-title">Bulk Actions:</span>
            <label className="tick-check-label" style={{ gap: '0' }}>
              <input type="checkbox" className="tick-check-input" id="bulk-rb-indicator" checked readOnly />
              <label htmlFor="bulk-rb-indicator" className="tick-check">
                <svg width="16px" height="16px" viewBox="0 0 18 18">
                  <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z" />
                  <polyline points="1 9 7 14 15 4" />
                </svg>
              </label>
            </label>
            <span>{selectedBills.size} item{selectedBills.size !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="bulk-actions-buttons slideout">
            <div>
              <button className="btn-slideout btn-danger" onClick={handleBulkDelete} disabled={bulkActionLoading}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">delete</span>
                </div>
                <span className="btn-label">Delete Selected</span>
              </button>
            </div>
            <div>
              <button data-read-safe className="btn-slideout" onClick={() => setSelectedBills(new Set())} disabled={bulkActionLoading}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">close</span>
                </div>
                <span className="btn-label">Clear Selection</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="repeating-bills-panel">
        <div className="table-toolbar">
          <button className="btn-secondary btn-sm" onClick={handleExpandAll}
            disabled={loading || expandingAll || bills.length === 0}>
            <span className="material-symbols-rounded">
              {expandedRows.size === bills.length && bills.length > 0 ? 'unfold_less' : 'unfold_more'}
            </span>
            {expandingAll ? 'Loading...' : (expandedRows.size === bills.length && bills.length > 0 ? 'Collapse All' : 'Expand All')}
          </button>

          {pagination.totalPages > 0 && (
            <PaginationControls pagination={pagination} onPageChange={changePage} />
          )}
        </div>

        <div className="table-container">
          <table className="data-table repeating-bills-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }} className="checkbox-col">
                  <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                    <input type="checkbox" className="tick-check-input" id="select-all-rb"
                      checked={bills.length > 0 && selectedBills.size === bills.length}
                      onChange={toggleSelectAll} disabled={bills.length === 0} />
                    <label htmlFor="select-all-rb" className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18">
                        <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z" />
                        <polyline points="1 9 7 14 15 4" />
                      </svg>
                    </label>
                  </label>
                </th>
                <th style={{ width: '34px' }}></th>
                <th className="sortable" onClick={() => handleSort('supplier_name')}>
                  Supplier {sortField === 'supplier_name' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th className="sortable" onClick={() => handleSort('reference')}>
                  Reference {sortField === 'reference' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th className="sortable" onClick={() => handleSort('schedule')}>
                  Frequency {sortField === 'schedule' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th className="sortable" onClick={() => handleSort('next_scheduled_date')}>
                  Next Date {sortField === 'next_scheduled_date' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th>End Date</th>
                <th className="text-right sortable" onClick={() => handleSort('total')}>
                  Total {sortField === 'total' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th className="status-col sortable" onClick={() => handleSort('status')}>
                  Status {sortField === 'status' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody className={loading ? 'loading-fade' : ''}>
              {bills.length === 0 && !loading ? (
                <tr><td colSpan="10" className="no-results">No repeating bills found</td></tr>
              ) : bills.length === 0 && loading ? (
                <tr><td colSpan="10"><LoadingSpinner /></td></tr>
              ) : (
                bills.map((bill) => (
                  <React.Fragment key={bill.id}>
                    <tr
                      className={`expandable-row ${selectedBills.has(bill.id) ? 'selected' : ''}`}
                      onClick={() => toggleRowExpand(bill.id)}
                    >
                      <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                        <label
                          className="tick-check-label"
                          style={{ justifyContent: 'center', gap: '0' }}
                          onMouseDown={dragSelect.onMouseDown(bill.id, selectedBills.has(bill.id))}
                          onMouseEnter={dragSelect.onMouseEnter(bill.id)}
                          onClick={(e) => e.preventDefault()}
                        >
                          <input type="checkbox" className="tick-check-input" id={`select-rb-${bill.id}`}
                            checked={selectedBills.has(bill.id)} onChange={() => toggleSelectBill(bill.id)} />
                          <label htmlFor={`select-rb-${bill.id}`} className="tick-check">
                            <svg width="16px" height="16px" viewBox="0 0 18 18">
                              <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z" />
                              <polyline points="1 9 7 14 15 4" />
                            </svg>
                          </label>
                        </label>
                      </td>
                      <td className="expand-cell">
                        <span className={`expand-icon ${expandedRows.has(bill.id) ? 'expanded' : ''}`}>▼</span>
                      </td>
                      <td>
                        <div>{bill.supplier_name || bill.account_company_name || '-'}</div>
                        {bill.account_no && (
                          <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)', marginTop: '2px' }}>
                            {bill.account_no}
                          </div>
                        )}
                      </td>
                      <td>{bill.reference || '-'}</td>
                      <td>{formatSchedule(bill)}</td>
                      <td>{formatDate(bill.next_scheduled_date)}</td>
                      <td>{bill.end_date ? formatDate(bill.end_date) : <span style={{ color: 'var(--text-muted)' }}>None</span>}</td>
                      <td className="currency">{formatCurrency(bill.total)}</td>
                      <td className="status-col">
                        <span className={getStatusBadgeClass(bill.status)}>{bill.status}</span>
                      </td>
                      <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                      </td>
                    </tr>
                    <tr className={`expanded-content ${expandedRows.has(bill.id) ? 'expanded' : ''}`}>
                      <td colSpan="10">
                        <div className="line-items-container">
                          {loadingLineItems[bill.id] ? (
                            <div className="loading-lines">Loading line items...</div>
                          ) : lineItems[bill.id]?.length > 0 ? (
                            <table className="line-items-table">
                              <thead>
                                <tr>
                                  <th>Description</th>
                                  <th>Item Code</th>
                                  <th className="text-right">Quantity</th>
                                  <th className="text-right">Unit Amount</th>
                                  <th className="text-right">Line Amount</th>
                                  <th style={{ textAlign: 'center' }}>Tax %</th>
                                  <th>Account</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lineItems[bill.id].map((line, idx) => (
                                  <tr key={idx}>
                                    <td>{line.description || '-'}</td>
                                    <td>{line.item_code || '-'}</td>
                                    <td className="text-right">{formatQuantity(line.quantity)}</td>
                                    <td className="text-right">{formatPrice(line.unit_amount)}</td>
                                    <td className="text-right">{formatCurrency(line.line_amount)}</td>
                                    <td style={{ textAlign: 'center' }}>{getTaxPercentDisplay(line.tax_type)}</td>
                                    <td>{line.account_code || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : expandedRows.has(bill.id) && !loadingLineItems[bill.id] ? (
                            <div className="no-lines">No line items found</div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedBillId && (
        <RepeatingBillModal
          billId={selectedBillId}
          onClose={() => { setSelectedBillId(null); searchRepeatingBills(pagination.page); }}
        />
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={() => {
          confirmDialog.onConfirm();
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />
    </div>
  );
}

export default SearchRepeatingBills;
